import { Checkbox, gridCellMultipleFocusableBehavior, Header, Table, Input, Button, TrashCanIcon, ExclamationCircleIcon, Loader, Tooltip, Text } from "@fluentui/react-northstar";
import { ThemePrepared } from "@fluentui/react-northstar";
import { useEffect, useRef, useState, ReactElement } from "react";
import { deleteCallGroupParticipant, addCallGroupParticipant, verifyCallGroupParticipant } from "../../apis/cyberGateManagementApi/cyberGateApiClient";
import { isSipAddressValid } from '../../utils/sipHelper'
import { useDebounce } from "../../hooks/useDebounce";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { NewTeamUser } from "./Types";
import { InputInEditMode } from "./InputInEditMode";

export interface CallGroupRow {
  callGroupId: string;
  name: string;
  description: string;
  participants: CallGroupParticipantRow[];
  isEdit: boolean;
  isCurrentUserSupervisor: boolean;
}

export interface CallGroupParticipantRow {
  participantId: string;
  teamsUserName: string;
  available: boolean;
  isCurrentUser: boolean;
}

interface CallGroupComponentProps {
  group: CallGroupRow;
  theme?: ThemePrepared | any;
  themeName: string;
  toogleEditebleMode: (groupId: string, editMode: boolean) => void;
  getCallGroupsUsers: () => void
  availabilityStateChanged(participantId: string): void;
}

const userLimit: number = 15
const userNameValidWarning: string = 'Not a valid Teams username'
const userBackendValidWarning: string = 'Teams username not valid within tenant'
const saveButtonWarning: string = 'One or more users are not valid'

export const CallGroupComponent = ({ group, availabilityStateChanged, toogleEditebleMode, getCallGroupsUsers: getCallGroupsUsers, theme, themeName }: CallGroupComponentProps) => {

  const [tableDataInEditMode, setTableDataInEditMode] = useState<CallGroupParticipantRow[]>([])
  const [editMode, setEditMode] = useState<boolean>(false)
  const [newTeamsUsers, setNewUsers] = useState<NewTeamUser[]>([])
  const [deletedUsersInEditMode, setDeletedUsersInEditMode] = useState<string[]>([])
  const [newUserName, setNewUserName] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false);
  const [isValidLoading, setIsValidLoading] = useState<boolean>(false)
  const [isUserValid, setUserValid] = useState<boolean>(false)

  const [isBackValid, setBackValid] = useState<boolean>(false)
  const [, setError] = useState<string>(); // ToDo: Error is set, but not used anywhere.

  const debouncedInput = useDebounce(newUserName, 350);
  const input = useRef<HTMLInputElement | null>(null)
  const { width } = useWindowWidth()
  const mobileView = 640
  const isUserUnique= newTeamsUsers.some(({ isUserValid, isBackValid }) => !isUserValid || !isBackValid)
  const tenants = tableDataInEditMode.length + newTeamsUsers.length
  const tenantsCounter = !newUserName ? tenants : tenants + 1
  const isUserLimit = tableDataInEditMode.length + newTeamsUsers.length < userLimit

  useEffect(() => {
    const container = document.querySelectorAll('[role="cell"]') as NodeListOf<HTMLElement>
    const emails = document.querySelectorAll('.emails') as NodeListOf<HTMLElement>

    // ToDo: This looks like a half-implemented feature...
    const defaultEmails = group.participants.map(el => el.teamsUserName)

    emails.forEach((mails, index) => {

      if (mails.offsetWidth < container[index].offsetWidth) {
        // mails.textContent = defaultEmails[index]
        // console.log(defaultEmails[index]);
      }

      if (mails.offsetWidth >= container[index].offsetWidth) {
        container[index].style.overflow = 'hidden'
        const [email] = mails.textContent?.split('@') as string[]
        mails.textContent = email
      }
    })

  }, [width, group])

  useEffect(() => {
    if (input){
      input.current?.focus()
    }
  }, [editMode, newTeamsUsers.length, tableDataInEditMode.length])

  useEffect(() => {
    if (!isValidLoading) {
      input.current?.focus()
    }
  }, [isValidLoading])

  useEffect(() => {
    const isValid = validateUserName(newUserName)
    setUserValid(isValid)

    if (isBackValid) {
      setBackValid(false)
    }

    if (isValid) {
      usersBackendValidation(newUserName)
    } 

  }, [debouncedInput])

  const availableCell = (entry: CallGroupParticipantRow) => {
    return {
      content: (
        !editMode ?
          <Checkbox toggle
            labelPosition='start'
            label={width > mobileView ? (entry.available ? "Available" : "Unavailable") : null}
            checked={entry.available}
            disabled={!entry.isCurrentUser && !group.isCurrentUserSupervisor}
            key={entry.participantId}
            onChange={(e, v) => {
              availabilityStateChanged(entry.participantId);
            }}
          />
          :
          <TrashCanIcon outline className="edit-del-icon" onClick={() => deleteUserInEditMode(entry)} />
      ),
      accessibility: gridCellMultipleFocusableBehavior,
    }
  }

  const createRows = (arr: CallGroupParticipantRow[]) => arr.map(entry => {
    return {
      key: entry.participantId,
      items: [
        {
          key: entry.participantId + '-username',
          content: <span className="emails">{entry.teamsUserName}</span>,
        },
        {
          key: entry.participantId + '-availabilitystatus',
          ...availableCell(entry)
        }
      ]
    }
  }
  );

  const addNewUser = () => {
    if (!newUserName || isValidLoading) {
      return;
    }

    if (!isUserValid || !isBackValid){
      return;
    }
    
    const newUser = { teamsUserName: newUserName, isUserValid, isBackValid}
    setNewUsers(data => [...data, newUser])
    setNewUserName('')
  }

  const deleteUserInEditMode = ({ participantId: participantId }: CallGroupParticipantRow) => {
    setTableDataInEditMode(data => data.filter(el => el.participantId !== participantId))
    setDeletedUsersInEditMode((users => [...users, participantId]))
  }

  const deleteNewUsers = (index: number) => setNewUsers(data => data.filter((_, i) => i !== index))

  const handleFormChange = (index: number, value: string) => {
    const isValid = validateUserName(value, index)
    const data = [...newTeamsUsers];
    data[index].teamsUserName = value
    data[index].isUserValid = isValid
    setNewUsers(data);
  }

  const validateUserName = (userName: string, currentIndex?: number) => {
    const state = [...newTeamsUsers, ...tableDataInEditMode].map(el => el.teamsUserName).filter((_, index) => index !== currentIndex)
    const isUserUnique = state.some(el => el.toLowerCase() === userName.toLowerCase())

    return !isUserUnique && isSipAddressValid(userName)
  }

  const usersBackendValidation = (userName: string) => {
    setIsValidLoading(true)
    verifyCallGroupParticipant(group.callGroupId, userName).then(({teamsUserNameValid}) => {
      setBackValid(teamsUserNameValid)
      setIsValidLoading(false)
    })
  }

  const saveUsersInEditMode = () => {
    setLoading(true)
    const groupID = group.callGroupId
    const deletedUsers = deletedUsersInEditMode.map(user => deleteCallGroupParticipant(groupID, user))
    const newUsers = newTeamsUsers.map(({ teamsUserName }) => addCallGroupParticipant(groupID, teamsUserName))
    const request = Promise.all([...deletedUsers, ...newUsers])
    request.then(data => {
    })
      .catch((error) => {
        setError(error);
        setLoading(false);
      })
      .finally(() => {
        getCallGroupsUsers()
        setLoading(false)
        setEditMode(false)
      })
  }

  const onEdit = () => {
    setEditMode(editMode => !editMode)
    setTableDataInEditMode([...group.participants])
    toogleEditebleMode(group.callGroupId, editMode)
  }

  const onSave = () => {
    toogleEditebleMode(group.callGroupId, editMode)
    saveUsersInEditMode()
    setTableDataInEditMode(data => {
      const teamUsers = newTeamsUsers.map(({teamsUserName}) => ({teamsUserName, available: false, isCurrentUser: false, participantId: ''}))
      return [...data, ...teamUsers]
    })
    clearEditModeData()
  }

  const onCancel = () => {
    setEditMode(false)
    toogleEditebleMode(group.callGroupId, editMode)
    setTableDataInEditMode([...group.participants])
    clearEditModeData()
    setNewUserName('')
  }

  const clearEditModeData = () => {
    setNewUsers([])
    setDeletedUsersInEditMode([])
  }

  const createTooltip = (userNameValid: boolean, backendValid: boolean, Component: ReactElement) => {
    const msg = !userNameValid ? userNameValidWarning : !backendValid ? userBackendValidWarning : null

    return <Tooltip unstable_pinned={true} unstable_disableTether='all' position='below' pointing align="center" subtle={false} content={msg} trigger={Component} />
  }

  const rows = editMode ? createRows(tableDataInEditMode) : createRows(group.participants)
  const header = {
    items: [<div className="table-title">Teams username</div>, <div className="table-title">Availability</div>]
  }

const inputBackgroundTheme = themeName === 'default' ? 'rgb(255, 255, 255)' : theme.siteVariables.colorScheme.default.background2
 
const isNewUsersEmpty = (!deletedUsersInEditMode.length && !newTeamsUsers.length) ? !isUserValid : false 
const validateSaveButton = isNewUsersEmpty || ((!isUserValid || !isBackValid) && !!newUserName) || isUserUnique

const warningValidateTooltip = createTooltip(isUserValid, isBackValid, <ExclamationCircleIcon styles={{ color: 'red'}} />)

  return (
    <>
    <div className={`wrapper-table ${themeName}`}>
      <div className="wrapper-header">
        <Header as="h3" content={group.description} className={`header-title ${themeName}`} />
        {group.isCurrentUserSupervisor && !group.isEdit &&
          <div className={`edit-btn-block ${themeName}`}>
            {!editMode ?
              <Button onClick={onEdit} content="Edit" /> : <div className="edit-btns">
                <Tooltip unstable_pinned={true} unstable_disableTether='all' pointing align="center" position='below' subtle={false} content={validateSaveButton ? ((!isBackValid || !isUserValid) && (newUserName || isUserUnique) ? saveButtonWarning : null) : null} trigger={<Button onClick={onSave} primary content="Save" loading={loading} disabledFocusable={validateSaveButton || isValidLoading} />} />
                <Button onClick={onCancel} content="Cancel" styles={{ marginLeft: 15 }} />
              </div>}
          </div>
        }
      </div>
      <Table header={width <= mobileView ? header : null} compact rows={rows} variables={{ rowBorderColor: 'transparent', minCellWidth: 'none' }} />
      <div className={`${editMode && !!tableDataInEditMode.length ? 'separator' : ''}`}>
        {editMode && newTeamsUsers.map((el, index) => (
          <InputInEditMode
            key={index}
            inputTheme={inputBackgroundTheme}
            handleFormChange={handleFormChange}
            deleteNewUsers={deleteNewUsers}
            index={index}
            setNewUsers={setNewUsers}
            isValid={el.isUserValid}
            isBackValid={el.isBackValid}
            createTooltip={createTooltip}
            groupId={group.callGroupId}
            userName={el.teamsUserName} />
        ))}
        {editMode && isUserLimit && <Input
          placeholder="Add Teams username..."
          fluid
          ref={input}
          value={newUserName}
          onBlur={addNewUser}
          disabled={isValidLoading}
          icon={!isUserValid && newUserName ? warningValidateTooltip : (isValidLoading ? <Loader size="smallest"/> : (!isBackValid && isUserValid ) ? warningValidateTooltip : null)}
          onKeyDown={(e) => e.keyCode === 13 ? addNewUser() : null}
          onChange={(e) => setNewUserName((e.target as HTMLInputElement).value)}
          variables={{ backgroundColor: inputBackgroundTheme, borderRadius: 'none' }}
        />}
      </div>
    </div>
    {editMode && <Text align='end' styles={{marginTop: 5}} content={`${tenantsCounter}/${userLimit}`} /> }
    </>
  );
}